/**
 * ============================================================================
 * PROGRESS BAR COMPONENT - 100% Ready with Animations
 * ============================================================================
 */

import React from 'react';

/**
 * ProgressBar Component
 * @param {Object} props
 * @param {number} props.value - Progress value (0-100)
 * @param {string} props.variant - Variant (default, success, warning, danger, info)
 * @param {string} props.size - Size (sm, md, lg)
 * @param {boolean} props.showLabel - Show percentage label
 * @param {string} props.label - Custom label
 * @param {boolean} props.animated - Enable animation
 * @param {string} props.className - Additional classes
 * @returns {JSX.Element}
 */
const ProgressBar = ({
  value = 0,
  variant = 'default',
  size = 'md',
  showLabel = false,
  label,
  animated = true,
  className = '',
}) => {
  const clampedValue = Math.min(Math.max(value, 0), 100);

  const sizeClasses = {
    sm: 'h-1',
    md: 'h-2',
    lg: 'h-3',
  };

  const variantClasses = {
    default: 'bg-blue-600 dark:bg-blue-500',
    success: 'bg-green-600 dark:bg-green-500',
    warning: 'bg-yellow-600 dark:bg-yellow-500',
    danger: 'bg-red-600 dark:bg-red-500',
    info: 'bg-cyan-600 dark:bg-cyan-500',
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Label */}
      {(showLabel || label) && (
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {label || 'Progress'}
          </span>
          {showLabel && (
            <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">
              {Math.round(clampedValue)}%
            </span>
          )}
        </div>
      )}

      {/* Progress Bar */}
      <div
        className={`
          w-full
          ${sizeClasses[size] || sizeClasses.md}
          bg-gray-200 dark:bg-gray-700
          rounded-full overflow-hidden
        `}
      >
        <div
          className={`
            h-full
            ${variantClasses[variant] || variantClasses.default}
            rounded-full
            transition-all duration-500 ease-out
            ${animated ? 'animate-fadeIn' : ''}
          `}
          style={{
            width: `${clampedValue}%`,
          }}
          role="progressbar"
          aria-valuenow={clampedValue}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  );
};

export default ProgressBar;
